/**
 * 发送验证码的自定义 Hook
 * 
 * 这是一个简易的发送验证码 Hook，只接收两个参数：
 * - account: 目标账户（邮箱或手机号）
 * - scene: 发送类型（场景）
 * 
 * 提供loading状态管理和错误处理
 */

import { useState, useCallback } from 'react'
import { APIWeb } from '@/api'
import { Toast } from 'react-vant'
import { useTranslation } from 'react-i18next'

/**
 * 发送验证码的场景类型
 * 对应 API type.d.ts 中的 SCENE 枚举
 */
export type SendCodeScene = 
  | 'balance_transfer'      // 余额转账
  | 'bank_transfer'         // 银行转账
  | 'bind_email'            // 绑定邮箱
  | 'bind_phone'            // 绑定手机
  | 'card_apply'            // 虚拟卡申请
  | 'card_cancel'           // 卡片注销
  | 'card_withdraw'         // 卡片转出
  | 'change_account'        // 变更账户
  | 'change_phone'          // 变更手机
  | 'cvv_view'              // CVV查看
  | 'password_reset'        // 密码重置
  | 'register'              // 注册
  | 'card_close'            // 卡片注销
  | 'change_google_auth'    // 更换google验证器
  | 'kyc_verify'            // KYC要素验证


/**
 * useSendCode Hook 的返回值类型
 */
export interface UseSendCodeReturn {
    /** 是否正在发送验证码 */
    sendCodeLoading: boolean
    /** 发送验证码的方法 */
    sendCode: (params: { account: string, scene: SendCodeScene, callBack?: () => void }) => Promise<SendCodeResponse | null>
}
export const useSendCode = (): UseSendCodeReturn => {
    const { t } = useTranslation()
    const [loading, setLoading] = useState(false)

    /**
     * 发送验证码
     * @param account 目标账户（邮箱或手机号）
     * @param scene 发送类型（场景）
     * @returns Promise<SendCodeResponse | null> 发送结果，失败时返回null
     */
    const sendCode = useCallback(async (params: { account: string, scene: SendCodeScene, callBack?: () => void }): Promise<SendCodeResponse | null> => {
        setLoading(true)
        try {
            // 先检查用户是否已验证
            // const verifiedResult = await APIWeb.checkVerified()
            // console.log(verifiedResult?.is_verified)
            // // is_verified = true 表示已验证，不需要重新发送验证码
            // if (verifiedResult?.is_verified) {
            //     // Toast.info(t('验证码仍然有效，无需重新发送'))
            //     // 返回一个模拟的成功结果，让后续流程继续
            //     params.callBack?.()
            //     return {} as SendCodeResponse
            // }

            // is_verified = false 表示需要发送验证码
            const result = await APIWeb.sendCode({
                account: params.account,
                scene: params.scene
            })
            Toast.success({
                message: t('验证码已发送'),
                onClose: params.callBack || (() => { })
            })
            return result
        } catch (error) {
            console.error('发送验证码失败:', error)
            return null
        } finally {
            setLoading(false)
        }
    }, [loading, t])

    /**
     * 重置loading状态
     */

    return {
        sendCodeLoading: loading,
        sendCode
    }
}

export default useSendCode